// charity-elegant — c07 Xayriya. A black-tie charity benefit guest page
// for the "MEHR-O-MUHABBAT GALA" (Mercy & Love Gala) — proceeds benefit
// the Orphan Education Fund.
//
// Design notes:
//   * Italiana display + warm cream (#F8F2E8) + antique gold (#8B6F47).
//     Mission-driven, restrained, evening-formal — never flashy.
//   * Signature: a tiny heart-cradled-in-hands SVG icon at the very top of
//     the hero gently pulses (opacity 0.6 ↔ 1.0 over 2s ease-in-out
//     infinite). `prefers-reduced-motion` snaps it to opaque-static.
//   * Three unique sections beyond the standard set:
//       1. Impact stats — four bordered tiles with figures
//       2. Auction Preview — three lots with photo, description, opening bid
//       3. Sponsorship Tiers — Bronze / Silver / Gold / Platinum row
//   * 5 sound triggers wired through @/lib/sound:
//       - chime → on RSVP "yes"
//       - tap   → on a sponsorship-tier hover
//       - ding  → on auction-bid commit
//       - music-on / music-off → on the ambient-music toggle
//       - swoosh → fired once when the impact stats reveal
//   * Ambient music is a synthesised pentatonic theme, not an audio file
//     (mirrors the pattern from restaurant-opening's do'mbira loop). On by
//     default? No — opt-in via the gold round toggle in the corner.
//   * All text is real Uzbek copy with English mission tagline. No
//     placeholders.

import { useEffect, useRef, useState } from "react";
import {
  GuestPhoto,
  CountdownSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
  StorySection,
  ScheduleSection,
  MapSection,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import type { VariantProps } from "./types";

// ─── Heart-with-hands SVG (signature pulser) ────────────────────────────
function HeartHands({
  accent,
  ink,
  size = 56,
}: {
  accent: string;
  ink: string;
  size?: number;
}) {
  return (
    <svg
      viewBox="0 0 64 56"
      width={size}
      height={(size * 56) / 64}
      aria-hidden
      style={{ display: "block" }}
    >
      <path
        d="M 6 36 Q 4 28 8 22 Q 12 18 18 22 Q 22 26 22 32 Q 22 38 26 42 Q 22 46 16 46 Q 8 46 6 36 Z"
        fill={accent}
        opacity="0.85"
      />
      <path
        d="M 58 36 Q 60 28 56 22 Q 52 18 46 22 Q 42 26 42 32 Q 42 38 38 42 Q 42 46 48 46 Q 56 46 58 36 Z"
        fill={accent}
        opacity="0.85"
      />
      <path
        d="M 32 14 C 28 6 18 8 18 18 C 18 26 28 32 32 36 C 36 32 46 26 46 18 C 46 8 36 6 32 14 Z"
        fill={accent}
      />
      <path
        d="M 24 14 Q 22 18 24 22"
        stroke="#FFFFFF"
        strokeWidth="1.2"
        fill="none"
        opacity="0.55"
        strokeLinecap="round"
      />
      <path
        d="M 32 14 C 28 6 18 8 18 18 C 18 26 28 32 32 36 C 36 32 46 26 46 18 C 46 8 36 6 32 14 Z"
        stroke={ink}
        strokeWidth="0.4"
        fill="none"
        opacity="0.35"
      />
    </svg>
  );
}

// ─── Reduced-motion detector ────────────────────────────────────────────
function useReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// ─── Reveal-on-scroll hook ──────────────────────────────────────────────
function useReveal<T extends HTMLElement>(onShow?: () => void) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  const cbRef = useRef(onShow);
  cbRef.current = onShow;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setShown(true);
      cbRef.current?.();
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      cbRef.current?.();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            cbRef.current?.();
            io.disconnect();
          }
        });
      },
      { threshold: 0.18 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

function revealStyle(shown: boolean): React.CSSProperties {
  return {
    opacity: shown ? 1 : 0,
    transform: shown ? "translateY(0)" : "translateY(24px)",
    transition: "opacity 700ms ease, transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
  };
}

// ─── Ambient pentatonic theme (synth, no audio files) ───────────────────
const GALA_NOTES = [261.63, 311.13, 349.23, 392.0, 466.16]; // C-Eb-F-G-Bb minor pentatonic — solemn yet warm

function useGalaTheme(enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (ctxRef.current && ctxRef.current.state === "running") {
        void ctxRef.current.suspend().catch(() => undefined);
      }
      return;
    }

    let cancelled = false;
    const start = async () => {
      if (!ctxRef.current) {
        const Ctor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctor) return;
        try {
          ctxRef.current = new Ctor();
        } catch {
          return;
        }
      }
      const c = ctxRef.current;
      if (!c) return;
      if (c.state === "suspended") {
        try {
          await c.resume();
        } catch {
          return;
        }
      }
      if (cancelled) return;

      const playPhrase = () => {
        const ctx = ctxRef.current;
        if (!ctx || ctx.state !== "running") return;
        const base = ctx.currentTime;
        GALA_NOTES.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          osc.type = "sine"; // soft, hall-like
          osc.frequency.setValueAtTime(freq, base + i * 0.55);
          const t0 = base + i * 0.55;
          const peak = 0.04;
          g.gain.setValueAtTime(0.0001, t0);
          g.gain.exponentialRampToValueAtTime(peak, t0 + 0.04);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
          osc.connect(g);
          g.connect(ctx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.55);
          osc.onended = () => {
            try {
              osc.disconnect();
              g.disconnect();
            } catch {
              /* noop */
            }
          };
        });
      };

      playPhrase();
      timerRef.current = window.setInterval(playPhrase, 5000);
    };
    void start();

    return () => {
      cancelled = true;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled]);

  // Cleanup the AudioContext on unmount.
  useEffect(() => {
    return () => {
      const c = ctxRef.current;
      ctxRef.current = null;
      if (c) {
        try {
          void c.close();
        } catch {
          /* noop */
        }
      }
    };
  }, []);
}

// ─── Auction lots (data) ────────────────────────────────────────────────
interface AuctionLot {
  title: string;
  artist: string;
  description: string;
  startBid: string;
  photoIdx: number;
}

// ─── Sponsorship tiers (data) ───────────────────────────────────────────
interface SponsorTier {
  name: string;
  badge: string;          // single-letter or roman, ornament for the tile
  price: string;
  perks: string[];
  emphasised?: boolean;   // gold tier gets a slight scale + heavier border
}

// ═══════════════════════════════════════════════════════════════════════
// Main variant
// ═══════════════════════════════════════════════════════════════════════
export function CharityElegantVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const reduce = useReducedMotion();
  const photos = t.photos || [];
  const heroPhoto = photos[0];

  // Music toggle state (off by default — black-tie events let guests opt in).
  const [musicOn, setMusicOn] = useState(false);
  useGalaTheme(musicOn);

  // Auction state — one current bid per lot. Bumps by ₩500K on commit.
  const [bids, setBids] = useState<Record<number, number>>({ 0: 0, 1: 0, 2: 0 });

  // Stats reveal swoosh — fires once on first intersection.
  const stats = useReveal<HTMLDivElement>(() => play("swoosh"));
  const auction = useReveal<HTMLDivElement>();
  const tiers = useReveal<HTMLDivElement>();

  // RSVP wrapper — chime on yes, tap on no.
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") {
      play("chime");
      window.setTimeout(() => play("tap"), 160);
    } else if (v === "no") {
      play("tap");
    }
    ctx.rsvp.setRsvp(v);
  };

  const placeBid = (lotIdx: number) => {
    play("ding");
    setBids((b) => ({ ...b, [lotIdx]: (b[lotIdx] ?? 0) + 1 }));
  };

  const toggleMusic = () => {
    if (musicOn) {
      play("music-off");
    } else {
      play("music-on");
    }
    setMusicOn((v) => !v);
  };

  // ─── Auction lot copy (Uzbek, with English titles where natural) ────
  const lots: AuctionLot[] = [
    {
      title: "«Tong shafagi» rasmi",
      artist: "Rassom: Aziza Karimova",
      description:
        "Suzanidan ilhomlangan zamonaviy kanvas, 80×100 sm. Daromadi to'liq jamg'armaga yo'naltiriladi.",
      startBid: "₩2,000,000",
      photoIdx: 1,
    },
    {
      title: "Kollektsion vino — 1998",
      artist: "Château privé · Frantsiya",
      description:
        "Anonim xayriyachidan sovg'a. Eski yog'ochda yetilgan, 0.75 l. Sertifikat bilan beriladi.",
      startBid: "₩3,500,000",
      photoIdx: 2,
    },
    {
      title: "Italiyaga ikki kishilik sayohat",
      artist: "Toskana, 7 kun",
      description:
        "Floranda 4-yulduzli mehmonxona, mahalliy gid xizmati va shaxsiy mehmondo'stlik kunlari.",
      startBid: "₩5,000,000",
      photoIdx: 0,
    },
  ];

  // ─── Sponsorship tiers ──────────────────────────────────────────────
  const sponsorTiers: SponsorTier[] = [
    {
      name: "Bronza",
      badge: "B",
      price: "₩5M",
      perks: [
        "1 stol uchun joy (8 kishi)",
        "Dasturda nom ko'rsatish",
        "Rahmat sertifikati",
      ],
    },
    {
      name: "Kumush",
      badge: "S",
      price: "₩12M",
      perks: [
        "1 VIP stol (10 kishi)",
        "Kechki ovqat menyusi tanlovi",
        "Maxsus yodgorlik sovg'asi",
        "Ijtimoiy tarmoqlarda eslatib o'tish",
      ],
    },
    {
      name: "Oltin",
      badge: "G",
      price: "₩25M",
      perks: [
        "2 VIP stol (20 kishi)",
        "Sahnaga taklif va minnatdorchilik so'zi",
        "Brending: bayroq va banner",
        "Xayriya hisoboti shaxsiy taqdim etiladi",
        "Mediadagi alohida e'tibor",
      ],
      emphasised: true,
    },
    {
      name: "Platina",
      badge: "P",
      price: "₩50M+",
      perks: [
        "3 VIP stol (30 kishi)",
        "Galani ochish huquqi",
        "Maktab nomi: «...nomidagi»",
        "Yillik hisobotda asosiy hamkor",
        "Maxsus mehmondo'stlik xizmati",
      ],
    },
  ];

  // ─── Schedule (Uzbek labels) ────────────────────────────────────────
  const schedule: Array<[string, string]> = [
    ["18:00", "Mehmonlarni qabul qilish · sham yoqish marosimi"],
    ["19:00", "Tantanali kechki ovqat"],
    ["20:30", "Asosiy nutq · jamg'arma rahbarining murojaati"],
    ["21:30", "Xayriya auksioni"],
    ["23:00", "Musiqa va raqs"],
  ];

  return (
    <div className="charity-variant">
      <style>{`
        @keyframes charityHeartPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.04); }
        }
        @keyframes charityShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .charity-pulse { animation: none !important; opacity: 1 !important; transform: none !important; }
          .charity-shimmer { animation: none !important; }
        }

        .charity-variant a:focus-visible,
        .charity-variant button:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 2px;
        }

        /* ═════════════════ MOBILE OVERRIDES (≤768px) ═════════════════
           Lock the elegant feel on small screens: smaller hero photo,
           single-column auction & tiers, tighter padding, full-width
           buttons. */
        @media (max-width: 768px) {
          .charity-variant button, .charity-variant a { min-height: 44px; }
          .charity-variant .charity-hero {
            padding: 64px 18px 48px !important;
          }
          .charity-variant .charity-hero-photo {
            height: min(60vh, 560px) !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          .charity-variant .charity-hero-name {
            font-size: clamp(36px, 12vw, 96px) !important;
            letter-spacing: 0 !important;
          }
          .charity-variant .charity-hero-tagline {
            font-size: 13px !important;
            letter-spacing: 0.18em !important;
          }
          .charity-variant .charity-section {
            padding: 64px 18px !important;
          }
          .charity-variant .charity-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          .charity-variant .charity-auction-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .charity-variant .charity-tier-grid {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          .charity-variant .charity-tier {
            transform: none !important;
          }
          .charity-variant .charity-music-toggle {
            top: 12px !important;
            right: 12px !important;
            width: 44px !important;
            height: 44px !important;
          }
          .charity-variant .charity-h2 {
            font-size: 36px !important;
          }
          .charity-variant .charity-stat-figure {
            font-size: 32px !important;
          }
        }
      `}</style>

      {/* ═══════════════ Music toggle (top-right, persistent) ═══════════════ */}
      <button
        type="button"
        onClick={toggleMusic}
        aria-label={musicOn ? "Musiqani o'chirish" : "Musiqani yoqish"}
        className="charity-music-toggle"
        style={{
          position: "fixed",
          top: 24,
          right: 24,
          zIndex: 50,
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: musicOn ? t.accent : t.bg,
          color: musicOn ? "#fff" : t.accent,
          border: `1.5px solid ${t.accent}`,
          cursor: "pointer",
          fontFamily: "var(--font-ui)",
          fontSize: 18,
          boxShadow: `0 6px 18px ${t.ink}22`,
          display: "grid",
          placeItems: "center",
          transition: "all 240ms ease",
        }}
      >
        {musicOn ? "♪" : "♫"}
      </button>

      {/* ═══════════════ HERO ═══════════════ */}
      <section
        className="charity-hero"
        style={{
          minHeight: "100vh",
          padding: "96px 48px 80px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          background: t.bg,
          color: t.ink,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Soft radial vignette so the hero feels candle-lit */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 50% 30%, ${t.accent}14 0%, transparent 60%)`,
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, maxWidth: 880, width: "100%" }}>
          {/* Pulsing heart-with-hands signature */}
          <div
            className="charity-pulse"
            style={{
              margin: "0 auto 28px",
              width: 76,
              animation: reduce
                ? "none"
                : "charityHeartPulse 2s ease-in-out infinite",
              willChange: "opacity, transform",
            }}
          >
            <HeartHands accent={t.accent} ink={t.ink} size={76} />
          </div>

          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.accent,
              marginBottom: 28,
            }}
          >
            CHARITY GALA · 2026
          </div>

          <h1
            className="charity-hero-name"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(56px, 9vw, 112px)",
              lineHeight: 1.02,
              fontWeight: 400,
              margin: 0,
              color: t.ink,
              letterSpacing: "0.005em",
            }}
          >
            {t.names}
          </h1>

          {/* Mission tagline (English) */}
          <div
            className="charity-hero-tagline"
            style={{
              marginTop: 24,
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 22,
              color: t.accent,
              letterSpacing: "0.02em",
            }}
          >
            Mercy &amp; Love — benefiting the Orphan Education Fund
          </div>

          {/* Hero past-gala photo */}
          <div
            className="charity-hero-photo"
            style={{
              marginTop: 48,
              height: 360,
              aspectRatio: "16 / 9",
              maxWidth: 760,
              marginLeft: "auto",
              marginRight: "auto",
              border: `1px solid ${t.accent}66`,
              boxShadow: `0 24px 60px ${t.ink}22`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <GuestPhoto
              src={heroPhoto}
              style={{
                width: "100%",
                height: "100%",
                filter: "sepia(0.18) saturate(0.92) brightness(0.96)",
              }}
              fallbackBg={t.bg}
              fallbackTone={t.accent}
            />
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(180deg, transparent 60%, ${t.ink}33 100%)`,
                pointerEvents: "none",
              }}
            />
          </div>

          {/* Date / dress code line */}
          <div
            style={{
              marginTop: 40,
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.32em",
              color: t.ink,
            }}
          >
            BLACK TIE · TASHKENT · 5 DECEMBER 2026
          </div>
        </div>
      </section>

      {/* ═══════════════ COUNTDOWN ═══════════════ */}
      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      {/* ═══════════════ STORY / MISSION ═══════════════ */}
      <StorySection
        t={t}
        eyebrow="OUR MISSION"
        title="200+ bolalar — sizning yordamingiz muhim"
        body={
          "«Mehr-o-muhabbat» jamg'armasi 2021-yildan beri O'zbekiston bo'ylab " +
          "yetim va boquvchisini yo'qotgan bolalarga ta'lim, sog'liq va " +
          "imkoniyatlar yetkazib kelmoqda. Har bir xayriya — bu maktab " +
          "kanselyariyasi, yangi kitob, qishki kiyim yoki yosh bola uchun " +
          "imtihon kursi. Bugun siz qilayotgan har bir tashabbus ertangi " +
          "kunda bir bola uchun ochiq eshik bo'lib qoladi. Galadan tushadigan " +
          "barcha mablag' to'liq bolalar ta'lim fondiga yo'naltiriladi — " +
          "sizning hissangiz haqida shaffof yillik hisobot e'lon qilinadi."
        }
      />

      {/* ═══════════════ UNIQUE 1: IMPACT STATS ═══════════════ */}
      <section
        ref={stats.ref}
        className="charity-section"
        style={{
          padding: "100px 48px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}08`,
          textAlign: "center",
          ...revealStyle(stats.shown),
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.42em",
            color: t.accent,
            marginBottom: 16,
          }}
        >
          BIZNING TA'SIRIMIZ
        </div>
        <h2
          className="charity-h2"
          style={{
            fontFamily: t.font,
            fontStyle: "italic",
            fontSize: 48,
            fontWeight: 400,
            marginBottom: 56,
            color: t.ink,
          }}
        >
          5 yilda nimaga erishdik
        </h2>

        <div
          className="charity-stats-grid"
          style={{
            maxWidth: 1080,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 24,
          }}
        >
          {(
            [
              { figure: "200+", label: "bolalar yordami olgan" },
              { figure: "₩8M", label: "jamg'arilgan" },
              { figure: "12", label: "maktab qurilgan" },
              { figure: "5", label: "yillik missiya" },
            ] as const
          ).map((s, i) => (
            <div
              key={i}
              style={{
                padding: "40px 20px",
                background: t.bg,
                border: `1px solid ${t.accent}55`,
                position: "relative",
                boxShadow: `inset 0 0 0 4px ${t.bg}, inset 0 0 0 5px ${t.accent}22`,
                transition: "transform 400ms ease",
                transform: stats.shown ? "translateY(0)" : "translateY(20px)",
                transitionDelay: `${i * 90}ms`,
              }}
            >
              <div
                className="charity-stat-figure"
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: 56,
                  fontWeight: 400,
                  color: t.accent,
                  lineHeight: 1,
                  marginBottom: 12,
                }}
              >
                {s.figure}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.22em",
                  color: t.ink,
                  textTransform: "uppercase",
                  lineHeight: 1.5,
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════ UNIQUE 2: AUCTION PREVIEW ═══════════════ */}
      <section
        ref={auction.ref}
        className="charity-section"
        style={{
          padding: "120px 48px",
          borderTop: `1px solid ${t.ink}22`,
          textAlign: "center",
          ...revealStyle(auction.shown),
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.42em",
            color: t.accent,
            marginBottom: 16,
          }}
        >
          KECHKI AUKSION
        </div>
        <h2
          className="charity-h2"
          style={{
            fontFamily: t.font,
            fontStyle: "italic",
            fontSize: 48,
            fontWeight: 400,
            marginBottom: 16,
            color: t.ink,
          }}
        >
          Lotlar oldindan ko'rish
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 16,
            color: `${t.ink}cc`,
            maxWidth: 560,
            margin: "0 auto 56px",
            lineHeight: 1.6,
          }}
        >
          Galada uchta noyob lotni taklif etamiz. Boshlang'ich qiymat
          ko'rsatilgan — kechqurun jonli auksionda yangi qiymat e'lon qilinadi.
        </p>

        <div
          className="charity-auction-grid"
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 32,
          }}
        >
          {lots.map((lot, i) => (
            <AuctionCard
              key={i}
              lot={lot}
              photo={photos[lot.photoIdx]}
              accent={t.accent}
              ink={t.ink}
              bg={t.bg}
              font={t.font}
              bidCount={bids[i] ?? 0}
              onBid={() => placeBid(i)}
            />
          ))}
        </div>
      </section>

      {/* ═══════════════ UNIQUE 3: SPONSORSHIP TIERS ═══════════════ */}
      <section
        ref={tiers.ref}
        className="charity-section"
        style={{
          padding: "120px 48px",
          borderTop: `1px solid ${t.ink}22`,
          background: `${t.accent}10`,
          textAlign: "center",
          ...revealStyle(tiers.shown),
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.42em",
            color: t.accent,
            marginBottom: 16,
          }}
        >
          HAMKORLIK DARAJALARI
        </div>
        <h2
          className="charity-h2"
          style={{
            fontFamily: t.font,
            fontStyle: "italic",
            fontSize: 48,
            fontWeight: 400,
            marginBottom: 16,
            color: t.ink,
          }}
        >
          Hamkor bo'ling
        </h2>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 16,
            color: `${t.ink}cc`,
            maxWidth: 560,
            margin: "0 auto 56px",
            lineHeight: 1.6,
          }}
        >
          To'rtta darajadan birini tanlang — har bir daraja brending,
          tantana va minnatdorchilik bilan ta'minlanadi.
        </p>

        <div
          className="charity-tier-grid"
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 20,
            alignItems: "stretch",
          }}
        >
          {sponsorTiers.map((tier, i) => (
            <SponsorTierCard
              key={i}
              tier={tier}
              accent={t.accent}
              ink={t.ink}
              bg={t.bg}
              font={t.font}
              onHover={() => play("tap")}
            />
          ))}
        </div>
      </section>

      {/* ═══════════════ SCHEDULE ═══════════════ */}
      <ScheduleSection t={t} items={schedule} />

      {/* ═══════════════ MAP ═══════════════ */}
      <MapSection
        t={t}
        isDark={isDark}
        addressLine="Hilton Tashkent City, Mustaqillik prospekti, Toshkent"
      />

      {/* ═══════════════ RSVP ═══════════════ */}
      <RsvpSection
        t={t}
        isDark={isDark}
        rsvp={ctx.rsvp.rsvp}
        setRsvp={wrappedSetRsvp}
        guests={ctx.rsvp.guests}
        setGuests={ctx.rsvp.setGuests}
        guestName={ctx.rsvp.guestName}
        setGuestName={ctx.rsvp.setGuestName}
        label="Birga bolalarga yordam beraylik"
      />

      <ShareSection t={t} />
      <GuestFooter t={t} />
    </div>
  );
}

// ─── Auction lot card ───────────────────────────────────────────────────
function AuctionCard({
  lot,
  photo,
  accent,
  ink,
  bg,
  font,
  bidCount,
  onBid,
}: {
  lot: AuctionLot;
  photo?: string;
  accent: string;
  ink: string;
  bg: string;
  font: string;
  bidCount: number;
  onBid: () => void;
}) {
  const [hover, setHover] = useState(false);
  // Each subsequent bid bumps the displayed price by ₩500K from the start.
  const baseBid = parseInt(lot.startBid.replace(/[^\d]/g, ""), 10);
  const currentBid = baseBid + bidCount * 500_000;
  const formatBid = (n: number) =>
    "₩" + (n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 1 : 2).replace(/\.0$/, "") + "M";

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: bg,
        border: `1px solid ${accent}66`,
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        boxShadow: hover
          ? `0 18px 36px ${ink}22`
          : `0 8px 20px ${ink}11`,
        transition: "box-shadow 320ms ease, transform 320ms ease",
        transform: hover ? "translateY(-4px)" : "translateY(0)",
      }}
    >
      <div
        style={{
          height: 200,
          aspectRatio: "16 / 10",
          position: "relative",
          overflow: "hidden",
          background: `${accent}22`,
          flexShrink: 0,
        }}
      >
        <GuestPhoto
          src={photo}
          style={{
            width: "100%",
            height: "100%",
            filter: "sepia(0.12) saturate(0.95)",
          }}
          fallbackBg={bg}
          fallbackTone={accent}
        />
        {/* Lot number badge */}
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            padding: "6px 12px",
            background: ink,
            color: bg,
            fontFamily: "var(--font-ui)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.28em",
          }}
        >
          LOT
        </div>
      </div>

      <div style={{ padding: "24px 22px 22px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 26,
            fontWeight: 400,
            margin: 0,
            color: ink,
            lineHeight: 1.2,
          }}
        >
          {lot.title}
        </h3>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.22em",
            color: accent,
            marginTop: 8,
            textTransform: "uppercase",
          }}
        >
          {lot.artist}
        </div>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 14,
            color: `${ink}cc`,
            lineHeight: 1.55,
            margin: "14px 0 22px",
            flex: 1,
          }}
        >
          {lot.description}
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: 16,
            borderTop: `1px solid ${accent}33`,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.28em",
                color: `${ink}88`,
                marginBottom: 4,
              }}
            >
              {bidCount > 0 ? "JORIY" : "BOSHLANG'ICH"}
            </div>
            <div
              style={{
                fontFamily: font,
                fontStyle: "italic",
                fontSize: 22,
                color: ink,
              }}
            >
              {formatBid(currentBid)}
            </div>
          </div>
          <button
            type="button"
            onClick={onBid}
            style={{
              padding: "10px 16px",
              minHeight: 44,
              background: accent,
              color: "#fff",
              border: `1px solid ${ink}`,
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.22em",
              cursor: "pointer",
              boxShadow: `3px 4px 0 -1px ${ink}`,
              transition: "transform 120ms ease",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseDown={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "translate(2px, 2px)";
            }}
            onMouseUp={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "translate(0, 0)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = "translate(0, 0)";
            }}
          >
            TAKLIF QO'SHISH
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sponsorship tier card ──────────────────────────────────────────────
function SponsorTierCard({
  tier,
  accent,
  ink,
  bg,
  font,
  onHover,
}: {
  tier: SponsorTier;
  accent: string;
  ink: string;
  bg: string;
  font: string;
  onHover: () => void;
}) {
  const [hover, setHover] = useState(false);
  const emphasised = tier.emphasised;

  return (
    <div
      className="charity-tier"
      onMouseEnter={() => {
        setHover(true);
        onHover();
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: emphasised ? "36px 24px 28px" : "32px 22px 26px",
        background: bg,
        border: `${emphasised ? 2 : 1}px solid ${accent}${emphasised ? "" : "66"}`,
        position: "relative",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        transform: hover
          ? `translateY(-6px) scale(${emphasised ? 1.04 : 1.02})`
          : `translateY(0) scale(${emphasised ? 1.02 : 1})`,
        transition: "transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 320ms ease",
        boxShadow: hover
          ? `0 22px 44px ${ink}22`
          : emphasised
            ? `0 12px 28px ${accent}33`
            : `0 6px 18px ${ink}11`,
      }}
    >
      {emphasised && (
        <div
          style={{
            position: "absolute",
            top: -12,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "4px 14px",
            background: accent,
            color: "#fff",
            fontFamily: "var(--font-ui)",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.32em",
            whiteSpace: "nowrap",
          }}
        >
          TAVSIYA ETILADI
        </div>
      )}

      {/* Badge medallion */}
      <div
        style={{
          width: 56,
          height: 56,
          margin: "0 auto",
          borderRadius: "50%",
          border: `1.5px solid ${accent}`,
          display: "grid",
          placeItems: "center",
          background: emphasised ? accent : "transparent",
          color: emphasised ? "#fff" : accent,
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 24,
          fontWeight: 400,
          boxShadow: emphasised ? `0 0 0 4px ${bg}, 0 0 0 5px ${accent}33` : "none",
        }}
      >
        {tier.badge}
      </div>

      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 28,
          fontWeight: 400,
          color: ink,
        }}
      >
        {tier.name}
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.32em",
          color: accent,
        }}
      >
        {tier.price}
      </div>

      <div style={{ height: 1, background: `${accent}33`, margin: "4px 0" }} />

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          flex: 1,
        }}
      >
        {tier.perks.map((perk, i) => (
          <li
            key={i}
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 13,
              color: `${ink}dd`,
              lineHeight: 1.5,
              paddingLeft: 18,
              position: "relative",
            }}
          >
            <span
              aria-hidden
              style={{
                position: "absolute",
                left: 0,
                top: 6,
                width: 6,
                height: 6,
                background: accent,
                transform: "rotate(45deg)",
              }}
            />
            {perk}
          </li>
        ))}
      </ul>
    </div>
  );
}
